import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Layout,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  Button,
  Banner,
  Badge,
  Box,
  ProgressBar,
  IndexTable,
  TextField,
  Select,
  Checkbox,
  Modal,
  Link,
  DropZone,
  Collapsible,
  Divider,
  Tooltip
} from '@shopify/polaris';
import { RefreshIcon, UploadIcon, ExternalIcon } from '@shopify/polaris-icons';
import { api, formatEur, formatEurCents, formatNumber, formatDate, formatDateTime, DOC_KIND_LABELS, GROUP_LABELS, sharePointFileUrl } from './shared.js';

const POLL_MS = 2000;

const STATUS_TONES = { extracted: 'success', pending: 'attention', failed: 'critical', missing: undefined, skipped: undefined };

function Stat({ label, value, help }) {
  return (
    <BlockStack gap="050">
      <Text as="span" variant="bodySm" tone="subdued">
        {label}
      </Text>
      <Text as="span" variant="headingLg">
        {value}
      </Text>
      {help && (
        <Text as="span" variant="bodySm" tone="subdued">
          {help}
        </Text>
      )}
    </BlockStack>
  );
}

function errorTone(pct) {
  const abs = Math.abs(pct);
  if (abs <= 10) return 'success';
  if (abs <= 25) return 'attention';
  return 'critical';
}

function JobCard({ job, onCancel }) {
  if (!job) return null;
  const running = job.status === 'running';
  const pct = job.progress.total ? Math.round((job.progress.done / job.progress.total) * 100) : running ? 5 : 100;
  const phaseLabel = { starting: 'Starting', listing: 'Listing documents', reading: 'Reading documents with AI', fitting: 'Fitting the model', saving: 'Saving the model' }[job.phase] || job.phase;
  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {running ? phaseLabel : 'Last training run'}
            </Text>
            <Badge tone={job.status === 'done' ? 'success' : job.status === 'failed' ? 'critical' : job.status === 'cancelled' ? 'warning' : 'info'}>{job.status}</Badge>
          </InlineStack>
          {running && (
            <Button tone="critical" variant="plain" onClick={onCancel} disabled={job.cancelRequested}>
              {job.cancelRequested ? 'Stopping…' : 'Stop'}
            </Button>
          )}
        </InlineStack>
        {running && <ProgressBar progress={pct} tone="primary" />}
        <InlineStack gap="400" wrap>
          <Text as="span" tone="subdued">
            Started {formatDateTime(job.startedAt)}
          </Text>
          {job.progress.total > 0 && (
            <Text as="span" tone="subdued">
              {job.progress.done}/{job.progress.total} documents · {job.progress.failed} failed
            </Text>
          )}
          <Text as="span" tone="subdued">
            AI cost ${job.costUsd.toFixed(2)}
          </Text>
        </InlineStack>
        {running && job.current?.length > 0 && (
          <Text as="p" variant="bodySm" tone="subdued" truncate>
            Reading: {job.current.join(' · ')}
          </Text>
        )}
        {job.result && (
          <Banner tone={job.result.activated ? 'success' : 'warning'}>
            Model #{job.result.modelId} {job.result.activated ? 'is active' : 'was saved but not activated (less accurate than the active one)'}: {job.result.samples} samples from {job.result.sources}{' '}
            documents
            {job.result.medianAbsErrorPct != null ? `, median error ${job.result.medianAbsErrorPct}%` : ''}
            {job.result.previousMedianAbsErrorPct != null ? ` (was ${job.result.previousMedianAbsErrorPct}%)` : ''}.
          </Banner>
        )}
        {job.error && <Banner tone="critical">{job.error}</Banner>}
        {job.log?.length > 0 && (
          <Box background="bg-surface-secondary" padding="200" borderRadius="200">
            <div style={{ maxHeight: 180, overflowY: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, lineHeight: '18px' }}>
              {[...job.log].reverse().map((entry, i) => (
                <div key={i} style={{ color: entry.level === 'error' ? '#c5280c' : entry.level === 'warning' ? '#8a6116' : undefined }}>
                  {entry.at.slice(11, 19)} {entry.text}
                </div>
              ))}
            </div>
          </Box>
        )}
      </BlockStack>
    </Card>
  );
}

function SettingsModal({ open, onClose, model, onApply, busy }) {
  const [values, setValues] = useState({});
  const [priors, setPriors] = useState({});
  useEffect(() => {
    if (!open || !model) return;
    const s = model.settings || {};
    setValues({ escalationPct: String(s.escalationPct ?? ''), halfLifeYears: String(s.halfLifeYears ?? ''), markupPct: String(s.markupPct ?? ''), ioReservePct: String(s.ioReservePct ?? '') });
    setPriors(Object.fromEntries((model.coefficients || []).map((c) => [c.key, String(c.prior)])));
  }, [open, model]);

  const apply = () => {
    const priorOverrides = {};
    (model.coefficients || []).forEach((c) => {
      const value = Number(String(priors[c.key]).replace(',', '.'));
      if (Number.isFinite(value) && value >= 0) priorOverrides[c.key] = { prior: value };
    });
    onApply({
      escalationPct: Number(values.escalationPct),
      halfLifeYears: Number(values.halfLifeYears),
      markupPct: Number(values.markupPct),
      ioReservePct: Number(values.ioReservePct),
      priorOverrides
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Model settings"
      primaryAction={{ content: 'Save and refit', onAction: apply, loading: busy }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
      large
    >
      <Modal.Section>
        <BlockStack gap="400">
          <InlineGrid columns={4} gap="300">
            <TextField
              label="Price increase per year"
              type="number"
              suffix="%"
              value={values.escalationPct}
              onChange={(v) => setValues((s) => ({ ...s, escalationPct: v }))}
              helpText="Brings old prices to today"
              autoComplete="off"
            />
            <TextField
              label="Half-life of a document"
              type="number"
              suffix="years"
              value={values.halfLifeYears}
              onChange={(v) => setValues((s) => ({ ...s, halfLifeYears: v }))}
              helpText="Older documents weigh less"
              autoComplete="off"
            />
            <TextField label="Default markup" type="number" suffix="%" value={values.markupPct} onChange={(v) => setValues((s) => ({ ...s, markupPct: v }))} autoComplete="off" />
            <TextField label="Default IO reserve" type="number" suffix="%" value={values.ioReservePct} onChange={(v) => setValues((s) => ({ ...s, ioReservePct: v }))} autoComplete="off" />
          </InlineGrid>
          <Divider />
          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">
              Default prices (used where the documents say little)
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Every feature starts from this price; training moves it towards what Elektro Tech charged. Features without data keep this price.
            </Text>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {(model?.coefficients || []).map((c) => (
                <TextField
                  key={c.key}
                  label={c.short}
                  type="number"
                  prefix="€"
                  suffix={`/ ${c.unit}`}
                  value={priors[c.key] ?? ''}
                  onChange={(v) => setPriors((s) => ({ ...s, [c.key]: v }))}
                  helpText={`learned: ${formatEur(c.value)}`}
                  autoComplete="off"
                />
              ))}
            </div>
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

function SourceModal({ sourceId, onClose, library, onChanged, notify }) {
  const [source, setSource] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!sourceId) return;
    setSource(null);
    setShowJson(false);
    api(`/training/sources/${sourceId}`)
      .then((data) => {
        setSource(data);
        setNote(data.AdminNote || '');
      })
      .catch((err) => notify(err.message, true));
  }, [sourceId]);

  const e = source?.extraction;
  const saveReview = async (patch) => {
    try {
      const updated = await api(`/training/sources/${sourceId}`, { method: 'PATCH', body: patch });
      setSource((s) => ({ ...s, ...updated }));
      onChanged();
    } catch (err) {
      notify(err.message, true);
    }
  };

  const files = (source?.FileNames || '').split(' | ').filter(Boolean);

  return (
    <Modal open={Boolean(sourceId)} onClose={onClose} title={source?.Title || source?.FileNames || 'Document'} large>
      <Modal.Section>
        {!source ? (
          <Text as="p">Loading…</Text>
        ) : (
          <BlockStack gap="400">
            <InlineStack gap="300" wrap>
              <Badge tone={STATUS_TONES[source.Status]}>{source.Status}</Badge>
              {source.DocKind && <Badge>{DOC_KIND_LABELS[source.DocKind] || source.DocKind}</Badge>}
              {source.ProjectRef && <Text as="span">Ref {source.ProjectRef}</Text>}
              <Text as="span">{formatDate(source.DocumentDate || source.RecordDate)}</Text>
              <Text as="span">{formatEurCents(source.TotalExclVat ?? source.RecordAmountExclVat)} excl. VAT</Text>
              {source.ExtractionModel && (
                <Text as="span" tone="subdued">
                  read by {source.ExtractionModel}
                  {source.ExtractionCostUsd != null ? ` ($${Number(source.ExtractionCostUsd).toFixed(3)})` : ''}
                </Text>
              )}
            </InlineStack>
            {files.map((file) => {
              const url = source.Origin === 'upload' ? null : sharePointFileUrl(library, source.Folder, file);
              return url ? (
                <Link key={file} url={url} target="_blank">
                  {file} ↗
                </Link>
              ) : (
                <Text key={file} as="span">
                  {file} {source.Origin === 'upload' ? '(uploaded)' : ''}
                </Text>
              );
            })}
            {source.Error && <Banner tone="critical">{source.Error}</Banner>}
            <InlineStack gap="400" blockAlign="end">
              <Checkbox label="Use this document for training" checked={source.Included !== false && source.Included !== 0} onChange={(value) => saveReview({ included: value })} />
              <Box minWidth="360px">
                <TextField
                  label="Note"
                  value={note}
                  onChange={setNote}
                  onBlur={() => note !== (source.AdminNote || '') && saveReview({ adminNote: note })}
                  autoComplete="off"
                  placeholder="Why it is excluded, what is special…"
                />
              </Box>
            </InlineStack>
            {e && (
              <>
                <InlineGrid columns={{ xs: 2, md: 5 }} gap="300">
                  <Stat label="Engineering" value={formatEur(e.engineering?.amount)} help={e.engineering?.hours ? `${formatNumber(e.engineering.hours)} h` : null} />
                  <Stat label="Assembly" value={formatEur(e.assembly?.amount)} help={e.assembly?.hours ? `${formatNumber(e.assembly.hours)} h` : null} />
                  <Stat label="Material" value={formatEur(e.materialAmount)} />
                  <Stat label="Delivery / site" value={formatEur((e.deliveryAmount || 0) + (e.siteWorkAmount || 0) || null)} />
                  <Stat label="Options" value={formatEur(e.optionsAmount)} />
                </InlineGrid>
                {e.cabinets?.length > 0 && (
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">
                      Cabinets the AI found
                    </Text>
                    {e.cabinets.map((c, i) => (
                      <Box key={i} padding="300" background="bg-surface-secondary" borderRadius="200">
                        <BlockStack gap="100">
                          <InlineStack align="space-between">
                            <InlineStack gap="200">
                              <Text as="span" fontWeight="semibold">
                                {c.name}
                              </Text>
                              <Badge tone={c.confidence === 'high' ? 'success' : c.confidence === 'medium' ? 'attention' : 'critical'}>{c.confidence}</Badge>
                            </InlineStack>
                            <Text as="span" fontWeight="semibold">
                              {formatEurCents(c.priceExclVat)}
                            </Text>
                          </InlineStack>
                          <Text as="p" variant="bodySm">
                            {c.kind} {c.widthMm ? `${c.widthMm}×${c.heightMm || '?'}×${c.depthMm || '?'} mm` : ''} · {c.incomingA ? `${c.incomingA} A` : 'incoming ?'} · CPU {c.plcCpu} · IO stations{' '}
                            {c.ioStations} · DI {c.di} / DO {c.do} / AI {c.ai} / AO {c.ao}
                            {c.fdi || c.fdo ? ` / F-DI ${c.fdi} / F-DO ${c.fdo}` : ''} · safety relays {c.safetyRelays}
                          </Text>
                          {c.motorFeeders?.length > 0 && (
                            <Text as="p" variant="bodySm" tone="subdued">
                              {c.motorFeeders.map((m) => `${m.qty}× ${m.starter} ${m.kw ?? '?'} kW`).join(' · ')}
                            </Text>
                          )}
                          {c.summary && (
                            <Text as="p" variant="bodySm" tone="subdued">
                              {c.summary}
                            </Text>
                          )}
                        </BlockStack>
                      </Box>
                    ))}
                  </BlockStack>
                )}
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">
                    What the model learns from it
                  </Text>
                  {source.samples?.length ? (
                    source.samples.map((s) => (
                      <BlockStack key={s.id} gap="050">
                        <InlineStack align="space-between">
                          <Text as="span">
                            {s.level} · {s.name}
                          </Text>
                          <Text as="span" tone={s.excludedReason ? 'subdued' : undefined}>
                            {formatEurCents(s.y)} · weight {s.weight}
                          </Text>
                        </InlineStack>
                        {s.excludedReason && (
                          <Text as="span" variant="bodySm" tone="caution">
                            Not used: {s.excludedReason}
                          </Text>
                        )}
                      </BlockStack>
                    ))
                  ) : (
                    <Text as="p" tone="subdued">
                      Nothing: {e.docKind === 'installment_invoice' ? 'an installment is only part of a project' : 'no priced cabinets or engineering in this document'}. It still feeds the price book
                      and the quote-versus-invoice comparison.
                    </Text>
                  )}
                </BlockStack>
                {e.notes && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    AI notes: {e.notes}
                  </Text>
                )}
                <Button variant="plain" onClick={() => setShowJson((v) => !v)}>
                  {showJson ? 'Hide raw extraction' : `Show raw extraction (${e.lineItems?.length || 0} lines)`}
                </Button>
                <Collapsible open={showJson} id="raw-extraction">
                  <pre style={{ maxHeight: 360, overflow: 'auto', fontSize: 11, background: '#f6f6f7', padding: 12, borderRadius: 8 }}>{JSON.stringify(e, null, 2)}</pre>
                </Collapsible>
              </>
            )}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

export default function TrainingTab({ overview, onModelChanged, notify }) {
  const [data, setData] = useState(null);
  const [activeModel, setActiveModel] = useState(null);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmFull, setConfirmFull] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openSourceId, setOpenSourceId] = useState(null);
  const [filter, setFilter] = useState({ q: '', status: '', kind: '' });
  const [showCoefficients, setShowCoefficients] = useState(true);
  const pollRef = useRef(null);

  const activeModelId = data?.models?.find((m) => m.IsActive)?.ModelID || null;

  const load = useCallback(async () => {
    try {
      const next = await api('/training');
      setData(next);
      setJob(next.job);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!activeModelId) {
      setActiveModel(null);
      return;
    }
    api(`/training/models/${activeModelId}`)
      .then((row) => setActiveModel(row.model))
      .catch((err) => setError(err.message));
  }, [activeModelId]);

  // poll a running job; reload everything when it ends
  useEffect(() => {
    if (job?.status !== 'running') return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await api(`/training/jobs/${job.id}`);
        if (cancelled) return;
        setJob(next);
        if (next.status !== 'running') {
          await load();
          onModelChanged();
          return;
        }
      } catch (err) {
        if (cancelled) return;
      }
      pollRef.current = setTimeout(tick, POLL_MS);
    };
    pollRef.current = setTimeout(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(pollRef.current);
    };
  }, [job?.id, job?.status]);

  const start = async (mode, settings) => {
    setBusy(true);
    try {
      const started = await api('/training/run', { method: 'POST', body: { mode, settings } });
      setJob(started.job);
      if (started.alreadyRunning) notify('A training run is already busy');
    } catch (err) {
      notify(err.message, true);
    } finally {
      setBusy(false);
      setConfirmFull(false);
      setSettingsOpen(false);
    }
  };

  const reread = async (sourceId) => {
    try {
      const started = await api(`/training/sources/${sourceId}/extract`, { method: 'POST' });
      setJob(started.job);
    } catch (err) {
      notify(err.message, true);
    }
  };

  const upload = async (files) => {
    const file = files[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const started = await api('/training/upload', { method: 'POST', body: form });
      setJob(started.job);
      notify(`${file.name} added: reading it now`);
      load();
    } catch (err) {
      notify(err.message, true);
    }
  };

  const activate = async (modelId) => {
    try {
      await api(`/training/models/${modelId}/activate`, { method: 'POST' });
      notify(`Model #${modelId} is active`);
      await load();
      onModelChanged();
    } catch (err) {
      notify(err.message, true);
    }
  };

  const sources = data?.sources || [];
  const filtered = useMemo(() => {
    const words = filter.q.toLowerCase().split(/\s+/).filter(Boolean);
    return sources.filter((s) => {
      if (filter.status && s.Status !== filter.status) return false;
      if (filter.kind && s.DocKind !== filter.kind) return false;
      const text = `${s.Title || ''} ${s.FileNames || ''} ${s.ProjectRef || ''} ${s.RecordNumber || ''}`.toLowerCase();
      return words.every((w) => text.includes(w));
    });
  }, [sources, filter]);

  const counts = useMemo(() => {
    const byStatus = {};
    sources.forEach((s) => (byStatus[s.Status] = (byStatus[s.Status] || 0) + 1));
    return byStatus;
  }, [sources]);

  const running = job?.status === 'running';
  const model = activeModel;
  const loo = model?.metrics?.leaveOneOut;

  if (error && !data) return <Banner tone="critical">{error}</Banner>;
  if (!data) return <Text as="p">Loading…</Text>;

  return (
    <Layout>
      <Layout.Section>
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="start" gap="300">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    Train on {data.supplier.name}&apos;s documents
                  </Text>
                  <Text as="p" tone="subdued">
                    Reads every quote (Prijsaanvraag), incoming invoice and credit note of supplier {String(data.supplier.klantnummer).padStart(5, '0')} in SharePoint with AI, then refits the prices.
                    Documents read before are not read again.
                  </Text>
                </BlockStack>
                <InlineStack gap="200">
                  <Button onClick={() => setSettingsOpen(true)} disabled={running || !model}>
                    Settings
                  </Button>
                  <Button onClick={() => setConfirmFull(true)} disabled={running}>
                    Re-read all
                  </Button>
                  <Button variant="primary" icon={RefreshIcon} loading={busy} disabled={running} onClick={() => start('incremental')}>
                    Retrain
                  </Button>
                </InlineStack>
              </InlineStack>
              {model ? (
                <InlineGrid columns={{ xs: 2, md: 5 }} gap="400">
                  <Stat label="Active model" value={`#${activeModelId}`} help={`trained ${formatDateTime(model.trainedAt)}`} />
                  <Stat
                    label="Documents used"
                    value={model.metrics.sources}
                    help={`${model.metrics.quoteSources ?? '?'} quotes · ${model.metrics.invoiceSources ?? '?'} invoices · ${model.metrics.samples} samples`}
                  />
                  <Stat
                    label="Median error on quotes"
                    value={loo?.byGroup?.quote?.medianAbsErrorPct != null ? `${loo.byGroup.quote.medianAbsErrorPct}%` : '—'}
                    help={loo?.byGroup?.quote?.documents ? `${loo.byGroup.quote.documents} unseen quotes · ${loo.byGroup.quote.within15Pct}% within ±15%` : 'no quotes yet'}
                  />
                  <Stat
                    label="Median error on invoices"
                    value={loo?.byGroup?.invoice?.medianAbsErrorPct != null ? `${loo.byGroup.invoice.medianAbsErrorPct}%` : '—'}
                    help={loo?.byGroup?.invoice?.documents ? `${loo.byGroup.invoice.documents} unseen invoices (noisier: real costs)` : 'no invoices yet'}
                  />
                  <Stat
                    label="Invoiced vs quoted"
                    value={
                      model.quoteVersusInvoiced?.medianDifferencePct != null ? `${model.quoteVersusInvoiced.medianDifferencePct > 0 ? '+' : ''}${model.quoteVersusInvoiced.medianDifferencePct}%` : '—'
                    }
                    help={model.quoteVersusInvoiced?.projects?.length ? `median of ${model.quoteVersusInvoiced.projects.length} projects` : 'no matching projects yet'}
                  />
                </InlineGrid>
              ) : (
                <Banner tone="info">No trained model yet: the estimator uses the built-in default prices. Press Retrain to read the documents.</Banner>
              )}
              {model?.rates?.[0] && (
                <Text as="p" variant="bodySm" tone="subdued">
                  Latest rates seen ({formatDate(model.rates[0].date)}): electrician {formatEurCents(model.rates[0].electrician)}/h · engineer {formatEurCents(model.rates[0].engineer)}/h
                </Text>
              )}
            </BlockStack>
          </Card>

          <JobCard job={job} onCancel={async () => setJob(await api(`/training/jobs/${job.id}/cancel`, { method: 'POST' }))} />

          {model && (
            <Card padding="0">
              <Box padding="400">
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="050">
                    <Text as="h2" variant="headingMd">
                      Learned prices
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Price per unit, at today&apos;s price level. “Data” is how much (weighted) evidence the documents gave; without data the default price stays.
                    </Text>
                  </BlockStack>
                  <Button variant="plain" onClick={() => setShowCoefficients((v) => !v)}>
                    {showCoefficients ? 'Hide' : 'Show'}
                  </Button>
                </InlineStack>
              </Box>
              <Collapsible open={showCoefficients} id="coefficients">
                <IndexTable
                  resourceName={{ singular: 'price', plural: 'prices' }}
                  itemCount={model.coefficients.length}
                  selectable={false}
                  headings={[
                    { title: 'Feature' },
                    { title: 'Group' },
                    { title: 'Default', alignment: 'end' },
                    { title: 'Learned', alignment: 'end' },
                    { title: '± (1 sd)', alignment: 'end' },
                    { title: 'Data', alignment: 'end' }
                  ]}
                >
                  {model.coefficients.map((c, index) => {
                    const change = c.prior > 0 ? Math.round(((c.value - c.prior) / c.prior) * 100) : null;
                    return (
                      <IndexTable.Row id={c.key} key={c.key} position={index}>
                        <IndexTable.Cell>
                          <Tooltip content={c.label}>
                            <Text as="span">
                              {c.short}{' '}
                              <Text as="span" tone="subdued">
                                / {c.unit}
                              </Text>
                            </Text>
                          </Tooltip>
                        </IndexTable.Cell>
                        <IndexTable.Cell>{GROUP_LABELS[c.group] || c.group}</IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text as="span" alignment="end" numeric tone="subdued">
                            {formatEurCents(c.prior)}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <InlineStack align="end" gap="200" blockAlign="center" wrap={false}>
                            {change != null && Math.abs(change) >= 5 && <Badge tone={change > 0 ? 'attention' : 'info'}>{`${change > 0 ? '+' : ''}${change}%`}</Badge>}
                            <Text as="span" numeric fontWeight="semibold">
                              {formatEurCents(c.value)}
                            </Text>
                          </InlineStack>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text as="span" alignment="end" numeric tone="subdued">
                            {formatEurCents(c.sd)}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <InlineStack align="end">
                            {c.support > 0 ? (
                              <Text as="span" numeric>
                                {formatNumber(c.support)}
                              </Text>
                            ) : (
                              <Badge>no data</Badge>
                            )}
                          </InlineStack>
                        </IndexTable.Cell>
                      </IndexTable.Row>
                    );
                  })}
                </IndexTable>
              </Collapsible>
            </Card>
          )}

          {loo?.rows?.length > 0 && (
            <Card padding="0">
              <Box padding="400">
                <BlockStack gap="050">
                  <Text as="h2" variant="headingMd">
                    Accuracy on documents it did not learn from
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Each document is left out once, the model is refitted without it and asked to price it (today&apos;s price level). Quotes are what the estimator predicts; invoices are real
                    post-calculated costs and scatter more (extra work, site hours).
                  </Text>
                </BlockStack>
              </Box>
              <IndexTable
                resourceName={{ singular: 'document', plural: 'documents' }}
                itemCount={loo.rows.length}
                selectable={false}
                headings={[
                  { title: 'Document' },
                  { title: 'Date' },
                  { title: 'Source' },
                  { title: 'What' },
                  { title: 'Elektro Tech', alignment: 'end' },
                  { title: 'Model', alignment: 'end' },
                  { title: 'Error', alignment: 'end' }
                ]}
              >
                {loo.rows.map((row, index) => (
                  <IndexTable.Row id={String(row.sourceId)} key={row.sourceId} position={index} onClick={() => setOpenSourceId(row.sourceId)}>
                    <IndexTable.Cell>
                      <div style={{ maxWidth: 380, whiteSpace: 'normal' }}>
                        <Text as="span">{row.title}</Text>
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{formatDate(row.date)}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={row.group === 'quote' ? 'info' : undefined}>{row.group === 'quote' ? 'Quote' : 'Invoice'}</Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{row.levels.join(', ')}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" alignment="end" numeric>
                        {formatEur(row.actual)}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" alignment="end" numeric>
                        {formatEur(row.predicted)}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack align="end">
                        <Badge tone={errorTone(row.errorPct)}>{`${row.errorPct > 0 ? '+' : ''}${row.errorPct}%`}</Badge>
                      </InlineStack>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </Card>
          )}

          <Card padding="0">
            <Box padding="400">
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Documents ({sources.length})
                  </Text>
                  <InlineStack gap="200">
                    {Object.entries(counts).map(([status, n]) => (
                      <Badge key={status} tone={STATUS_TONES[status]}>{`${n} ${status}`}</Badge>
                    ))}
                  </InlineStack>
                </InlineStack>
                <InlineStack gap="300" wrap={false}>
                  <Box minWidth="280px">
                    <TextField
                      label="Search"
                      labelHidden
                      value={filter.q}
                      onChange={(q) => setFilter((f) => ({ ...f, q }))}
                      placeholder="Search title, file, reference"
                      clearButton
                      onClearButtonClick={() => setFilter((f) => ({ ...f, q: '' }))}
                      autoComplete="off"
                    />
                  </Box>
                  <Select
                    label="Status"
                    labelHidden
                    value={filter.status}
                    onChange={(status) => setFilter((f) => ({ ...f, status }))}
                    options={[{ label: 'All statuses', value: '' }, ...['extracted', 'pending', 'failed', 'missing'].map((s) => ({ label: s, value: s }))]}
                  />
                  <Select
                    label="Kind"
                    labelHidden
                    value={filter.kind}
                    onChange={(kind) => setFilter((f) => ({ ...f, kind }))}
                    options={[{ label: 'All kinds', value: '' }, ...Object.entries(DOC_KIND_LABELS).map(([value, label]) => ({ label, value }))]}
                  />
                </InlineStack>
              </BlockStack>
            </Box>
            <IndexTable
              resourceName={{ singular: 'document', plural: 'documents' }}
              itemCount={filtered.length}
              selectable={false}
              headings={[
                { title: 'Document' },
                { title: 'Kind' },
                { title: 'Date' },
                { title: 'Total', alignment: 'end' },
                { title: 'Cabinets', alignment: 'end' },
                { title: 'Status' },
                { title: '' }
              ]}
            >
              {filtered.slice(0, 300).map((s, index) => {
                const included = s.Included !== false && s.Included !== 0;
                return (
                  <IndexTable.Row id={String(s.SourceID)} key={s.SourceID} position={index}>
                    <IndexTable.Cell>
                      <div style={{ maxWidth: 380, whiteSpace: 'normal', opacity: included ? 1 : 0.5 }}>
                        <BlockStack gap="050">
                          <Button variant="plain" onClick={() => setOpenSourceId(s.SourceID)}>
                            {s.Title || s.FileNames}
                          </Button>
                          <Text as="span" variant="bodySm" tone="subdued">
                            {s.Origin === 'upload' ? 'Uploaded · ' : `${(s.Folder || '').split('/').slice(2).join(' / ')} · `}
                            {s.FileNames}
                          </Text>
                        </BlockStack>
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{s.DocKind ? <Badge>{DOC_KIND_LABELS[s.DocKind] || s.DocKind}</Badge> : '—'}</IndexTable.Cell>
                    <IndexTable.Cell>{formatDate(s.DocumentDate || s.RecordDate)}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" alignment="end" numeric>
                        {formatEur(s.TotalExclVat ?? s.RecordAmountExclVat)}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" alignment="end" numeric>
                        {s.CabinetCount ?? '—'}
                      </Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="100">
                        <Badge tone={STATUS_TONES[s.Status]}>{s.Status}</Badge>
                        {!included && <Badge tone="warning">excluded</Badge>}
                      </InlineStack>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="200" align="end" wrap={false}>
                        <Button size="slim" variant="plain" onClick={() => setOpenSourceId(s.SourceID)}>
                          View
                        </Button>
                        <Button size="slim" variant="plain" disabled={running || s.Status === 'missing'} onClick={() => reread(s.SourceID)}>
                          Re-read
                        </Button>
                      </InlineStack>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          </Card>
        </BlockStack>
      </Layout.Section>

      <Layout.Section variant="oneThird">
        <BlockStack gap="400">
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Add a quote
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Got an Elektro Tech quote by e-mail that is not in the system? Add the PDF: it is read and the model is refitted.
              </Text>
              <DropZone accept="application/pdf,.pdf" allowMultiple={false} onDrop={upload} disabled={running}>
                <DropZone.FileUpload actionTitle="Add PDF" actionHint="Elektro Tech quote" />
              </DropZone>
            </BlockStack>
          </Card>

          <Card padding="0">
            <Box padding="400">
              <Text as="h2" variant="headingMd">
                Model versions
              </Text>
            </Box>
            <IndexTable
              resourceName={{ singular: 'model', plural: 'models' }}
              itemCount={data.models.length}
              selectable={false}
              headings={[{ title: '#' }, { title: 'Trained' }, { title: 'Error', alignment: 'end' }, { title: '' }]}
              emptyState={
                <Box padding="400">
                  <Text as="p" tone="subdued">
                    No models yet.
                  </Text>
                </Box>
              }
            >
              {data.models.map((m, index) => (
                <IndexTable.Row id={String(m.ModelID)} key={m.ModelID} position={index}>
                  <IndexTable.Cell>
                    <InlineStack gap="100">
                      <Text as="span">#{m.ModelID}</Text>
                      {m.IsActive ? <Badge tone="success">active</Badge> : null}
                    </InlineStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <BlockStack gap="0">
                      <Text as="span" variant="bodySm">
                        {formatDateTime(m.CreatedAt)}
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {m.SourceCount ?? 0} docs · {m.Note || ''} {m.CreatedByName ? `· ${m.CreatedByName}` : ''}
                      </Text>
                    </BlockStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" alignment="end" numeric>
                      {m.MedianErrorPct != null ? `${Number(m.MedianErrorPct)}%` : '—'}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {!m.IsActive && (
                      <Button size="slim" variant="plain" onClick={() => activate(m.ModelID)} disabled={running}>
                        Activate
                      </Button>
                    )}
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </Card>

          {model?.quoteVersusInvoiced?.projects?.length > 0 && (
            <Card>
              <BlockStack gap="300">
                <BlockStack gap="050">
                  <Text as="h2" variant="headingMd">
                    Quoted versus invoiced
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Elektro Tech quotes are estimates on time-and-material basis; this is what was finally invoiced per project.
                  </Text>
                </BlockStack>
                {model.quoteVersusInvoiced.projects.map((p) => (
                  <InlineStack key={p.ref} align="space-between" blockAlign="center" wrap={false}>
                    <BlockStack gap="0">
                      <Text as="span" variant="bodySm" fontWeight="semibold">
                        {p.customerProject || p.ref}
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {p.ref} · {formatEur(p.quoted)} → {formatEur(p.invoiced)}
                      </Text>
                    </BlockStack>
                    <Badge tone={errorTone(p.differencePct)}>{`${p.differencePct > 0 ? '+' : ''}${p.differencePct}%`}</Badge>
                  </InlineStack>
                ))}
              </BlockStack>
            </Card>
          )}

          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">
                How training works
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                The AI reads each document once: cabinets, IO counts, motor feeders, hours, rates and every priced line. Quotes with a price per cabinet teach the most; invoices with a
                post-calculation count half; older documents are brought to today&apos;s prices ({model?.settings?.escalationPct ?? 3}% per year) and weigh less. Exclude a document when it misleads
                the model, then press Retrain.
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                SharePoint library: {data.library}
              </Text>
            </BlockStack>
          </Card>
        </BlockStack>
      </Layout.Section>

      <Modal
        open={confirmFull}
        onClose={() => setConfirmFull(false)}
        title="Read every document again?"
        primaryAction={{ content: 'Re-read all', onAction: () => start('full'), loading: busy }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setConfirmFull(false) }]}
      >
        <Modal.Section>
          <Text as="p">
            All {sources.length} documents are read by AI again (a few minutes, roughly ${Math.max(1, Math.round(sources.length * 0.01))} of AI usage). Only needed after the reading instructions
            changed; normal retraining reads new documents only.
          </Text>
        </Modal.Section>
      </Modal>
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} model={model} busy={busy} onApply={(settings) => start('refit', settings)} />
      <SourceModal sourceId={openSourceId} onClose={() => setOpenSourceId(null)} library={data.library} notify={notify} onChanged={load} />
    </Layout>
  );
}
